/**
 * Day 3, discovery: a failed provider attempt and its superseding retry, the
 * Offer the bounded queries run from, the target context, and the queries
 * with their provider usage.
 */

import type { ReactNode } from 'react';

import {
  discoverAnalysisCompetitors,
  newIdempotencyKey,
  retryAnalysisCompetitorDiscovery,
  type AnalysisDay2State,
  type AnalysisDay3State,
  type AnalysisRunSummary,
  type AnalysisStageAttempt,
} from '../../api.js';
import { Button, InlineAlert } from '../../ui.js';
import { card, selectStyle } from './run-page-styles.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { CompetitorForms } from './useCompetitorForms.js';

export function CompetitorDiscoveryPanel({
  id,
  run,
  attempts,
  day2,
  day3,
  busy,
  mutate,
  forms,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly attempts: readonly AnalysisStageAttempt[];
  readonly day2: AnalysisDay2State;
  readonly day3: AnalysisDay3State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly forms: CompetitorForms;
}): ReactNode {
  const { selectedOfferId, setSelectedOfferId } = forms;

  return (
    <>
      {(() => {
        // The latest discovery attempt, if it failed on the provider side.
        // Technical failures park the run at COMPETITOR_DISCOVERY/FAILED
        // and are resolved with a superseding same-run retry — they are
        // provider incidents, never evidence conclusions.
        const discoveryAttempts = attempts.filter(
          (attempt) => attempt.stage === 'COMPETITOR_DISCOVERY',
        );
        const latestDiscovery = discoveryAttempts[discoveryAttempts.length - 1];
        if (
          run.currentStage !== 'COMPETITOR_DISCOVERY' ||
          latestDiscovery === undefined ||
          latestDiscovery.executionState !== 'FAILED' ||
          latestDiscovery.errorCode === null
        ) {
          return null;
        }
        return (
          <article style={{ ...card, marginBottom: 12 }}>
            <InlineAlert
              tone="error"
              title={`Discovery failed: ${latestDiscovery.errorCode}`}
            >
              {latestDiscovery.errorDetail ??
                'The search provider failed before candidate normalization.'}{' '}
              This is a technical provider failure, not evidence insufficiency. Retrying
              creates a superseding discovery attempt in this same run.
            </InlineAlert>
            <Button
              variant="primary"
              busy={busy === 'retry-discovery'}
              onClick={() =>
                void mutate('retry-discovery', () =>
                  retryAnalysisCompetitorDiscovery(id, newIdempotencyKey()),
                )
              }
            >
              Retry discovery
            </Button>
          </article>
        );
      })()}

      {run.currentStage === 'COMPETITOR_DISCOVERY' ? (
        <div style={card}>
          <label>
            <span>Target Offer / scope</span>
            <select
              value={selectedOfferId}
              onChange={(event) => {
                setSelectedOfferId(event.target.value);
              }}
              style={selectStyle}
            >
              {(day2.approvedProfile?.payload.offers ?? []).map((offer) => (
                <option key={offer.localId} value={offer.localId}>
                  {offer.title.value ?? 'Untitled Offer'}
                </option>
              ))}
            </select>
          </label>
          <p style={{ color: 'var(--text-muted)' }}>
            Mouja will run 4–8 bilingual, Saudi-focused queries built from this approved
            Offer. Search rank never confirms a competitor.
          </p>
          <Button
            variant="primary"
            busy={busy === 'discover-competitors'}
            disabled={!selectedOfferId}
            onClick={() =>
              void mutate('discover-competitors', () =>
                discoverAnalysisCompetitors(id, newIdempotencyKey(), selectedOfferId),
              )
            }
          >
            Discover and qualify candidates
          </Button>
        </div>
      ) : null}

      {day3.targetScope !== null ? (
        <article style={{ ...card, marginTop: 12 }}>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>Target context</h3>
          <dl className="mj-form-two-column">
            <div>
              <dt>Business</dt>
              <dd>{day3.targetScope.payload.businessName}</dd>
            </div>
            <div>
              <dt>Offer</dt>
              <dd>{day3.targetScope.payload.offerTitle}</dd>
            </div>
            <div>
              <dt>Audience</dt>
              <dd>{day3.targetScope.payload.audiences.join(', ') || 'Unknown'}</dd>
            </div>
            <div>
              <dt>Need</dt>
              <dd>{day3.targetScope.payload.needs.join(', ') || 'Unknown'}</dd>
            </div>
            <div>
              <dt>Geography</dt>
              <dd>{day3.targetScope.payload.geography ?? 'Unknown'}</dd>
            </div>
            <div>
              <dt>Delivery</dt>
              <dd>{day3.targetScope.payload.deliveryMode ?? 'Unknown'}</dd>
            </div>
            <div>
              <dt>Positioning</dt>
              <dd>{day3.targetScope.payload.positioning ?? 'Unknown'}</dd>
            </div>
          </dl>
        </article>
      ) : null}

      {day3.queries.length > 0 ? (
        <article style={{ ...card, marginTop: 12 }}>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>Discovery</h3>
          <p>
            {day3.queries.length} bounded queries · {day3.candidates.length} normalized
            candidates
          </p>
          <ul>
            {day3.queries.map((query) => (
              <li key={query.id}>
                <bdi>{query.queryText}</bdi> · {query.language.toUpperCase()} ·{' '}
                {query.resultCount ?? 0} result(s)
                {query.errorCode ? ` · ${query.errorCode}` : ''}
              </li>
            ))}
          </ul>
          {day3.queries.some((query) => query.errorDetail) ? (
            <InlineAlert
              tone="warning"
              title="Automated discovery is unavailable; manual candidate entry remains available."
            />
          ) : null}
          {day3.usage.map((entry) => (
            <p key={entry.id} style={{ color: 'var(--text-muted)' }}>
              {entry.provider} · {entry.operation} · {entry.resultState ?? 'unknown'} ·{' '}
              {entry.costMicros === null
                ? 'cost unknown'
                : `$${(Number(entry.costMicros) / 1_000_000).toFixed(4)} ${entry.costBasis}`}
            </p>
          ))}
        </article>
      ) : null}
    </>
  );
}
